// Variations panel — generates N seed variants of the current composition
// and lets the user pick one. Like a "remix" of what you already have.

import { mulberry32 } from '@/lib/rng';
import { state, replaceState } from '@/core/state';
import { createPipeline } from '@/core/pipeline';
import type { AppState, Layer } from '@/core/types';
import { el, on } from '@/lib/dom';

const N_VARIANTS = 8;
const THUMB_PX = 120;

export function openVariations(): void {
  const baseState = state.peek();
  if (baseState.layers.length === 0) return;

  const dim = el('div', { class: 'modal-backdrop' });
  const dialog = el('div', { class: 'modal modal-presets', role: 'dialog', ariaLabel: 'Variations' });

  const head = el('div', { class: 'modal-head' }, [
    el('div', { class: 'modal-title' }, ['Variations']),
    el('div', { class: 'modal-subtitle' }, ['Eight reseeded takes on your current stack. Click one to apply.']),
  ]);
  const grid = el('div', { class: 'preset-grid' });
  dialog.append(head, grid);
  dim.appendChild(dialog);
  document.body.appendChild(dim);

  // Generate variants and render each
  const seedRng = mulberry32(Date.now() & 0xffffffff);
  for (let i = 0; i < N_VARIANTS; i++) {
    const variant = makeVariant(baseState, Math.floor(seedRng() * 0x7fffffff));
    grid.appendChild(buildCard(variant, () => {
      replaceState(variant, true);
      close();
    }));
  }

  function close(): void {
    dim.remove();
    offKey();
    offClick();
  }
  const offKey = on(window, 'keydown', (ev) => {
    if (ev.key === 'Escape') close();
  });
  const offClick = on(dim, 'click', (ev) => {
    if (ev.target === dim) close();
  });
}

function makeVariant(base: AppState, seed: number): AppState {
  const rng = mulberry32(seed);
  const layers: Layer[] = base.layers.map((l) => ({
    ...l,
    id: `v_${Math.floor(rng() * 1e9).toString(36)}`,
    seed: Math.floor(rng() * 0x7fffffff),
  }));
  return {
    ...base,
    layers,
    selectedLayerId: layers[0]?.id ?? null,
  };
}

function buildCard(variant: AppState, onPick: () => void): HTMLElement {
  const canvas = el('canvas', { class: 'preset-thumb' }) as HTMLCanvasElement;
  canvas.width = THUMB_PX;
  canvas.height = THUMB_PX;
  const card = el('button', { class: 'preset-card', ariaLabel: 'Apply variation' }, [canvas]);
  on(card, 'click', onPick);
  void renderInto(variant, canvas);
  return card;
}

async function renderInto(state: AppState, canvas: HTMLCanvasElement): Promise<void> {
  const pipeline = createPipeline();
  const sub: AppState = { ...state, resolution: { w: THUMB_PX, h: THUMB_PX }, soloLayerId: null };
  const result = await pipeline.render(sub);
  if (!result.image) return;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.imageSmoothingEnabled = false;
  ctx.putImageData(result.image, 0, 0);
}
